import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 dark:bg-[rgba(6,28,20,0.85)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideClose, priority, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay
      className={cn(
        "fixed inset-0 z-50 bg-black/80 dark:bg-[rgba(6,28,20,0.85)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        priority && "z-[9998]",
        overlayClassName
      )}
    />
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6",
        priority && "z-[9999]"
      )}
    >
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 grid w-full max-w-lg max-h-[90vh] gap-4 overflow-y-auto border border-slate-200 dark:border-[rgba(142,218,182,0.12)] bg-white dark:bg-[rgba(11,31,24,0.98)] p-6 shadow-lg dark:shadow-[0_18px_48px_rgba(0,0,0,0.35)] duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg backdrop-blur-sm",
          priority && "z-[9999]",
          className
        )}
        {...props}>
        <div className="flex min-h-0 w-full max-w-full flex-1 flex-col overflow-x-hidden">
          {children}
        </div>
        {!hideClose && (
          <DialogPrimitive.Close
            className="absolute right-2 top-2 sm:right-4 sm:top-4 z-20 flex h-10 w-10 sm:h-9 sm:w-9 items-center justify-center rounded-full opacity-90 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-slate-400 dark:ring-emerald-500/40 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-slate-100 dark:data-[state=open]:bg-[rgba(142,218,182,0.14)] data-[state=open]:text-slate-600 dark:data-[state=open]:text-[#EAF7F0]"
            style={{ marginTop: 'max(0px, env(safe-area-inset-top))' }}
          >
            <X className="h-5 w-5 text-slate-600 dark:text-[#EAF7F0]" strokeWidth={2.25} />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </div>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left text-slate-900 dark:text-[#EAF7F0]", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight text-slate-900 dark:text-[#EAF7F0]", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-slate-500 dark:text-[#C7DDD0]", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
